import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { axiosInstance } from 'axiosInstance';
import { API_ROUTES } from 'constants/api';
import { SliceStatus } from 'types';
import type { ApiErrorCode } from 'types/errorCodes';
import { isApiError } from 'types/errorCodes';
import type { UserSegmentRecap } from 'types/userSegment';

import type { RootState } from '../store';

export interface IUserSegmentState {
    userSegmentRecap: UserSegmentRecap | null;
    status: SliceStatus;
    error?: ApiErrorCode;
}

export const initialState: IUserSegmentState = {
    status: SliceStatus.IDLE,
    userSegmentRecap: null,
};

interface FetchUserSegmentRecapSuccessResponse {
    userSegments: UserSegmentRecap | null;
}

export const fetchUserSegmentData = createAsyncThunk<
    FetchUserSegmentRecapSuccessResponse,
    { userId: number },
    { rejectValue: ApiErrorCode }
>('user_segment/fetchUserSegmentRecap', async (params: { userId: number }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get<FetchUserSegmentRecapSuccessResponse>(
            `${API_ROUTES.USER_SEGMENT}recap/${params.userId}`,
        );
        return response.data;
    } catch (err: unknown) {
        let apiErrorCode: ApiErrorCode = 'Generic';

        if (axios.isAxiosError(err) && err.response && isApiError(err.response.data)) {
            const body = err.response.data;
            apiErrorCode = body.errorCode;
        }

        return rejectWithValue(apiErrorCode);
    }
});

const userSegmentSlice = createSlice({
    name: 'userSegment',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(fetchUserSegmentData.pending, (state) => {
            state.status = SliceStatus.LOADING;
        });
        builder.addCase(fetchUserSegmentData.fulfilled, (state, { payload }) => {
            state.userSegmentRecap = payload.userSegments;
            state.status = SliceStatus.SUCCEDED;
            state.error = undefined;
        });
        builder.addCase(fetchUserSegmentData.rejected, (state, action) => {
            state.status = SliceStatus.FAILED;
            state.error = action.payload;
            state.userSegmentRecap = null;
        });
    },
});

export const selectUserSegments = (state: RootState) => state.userSegmentSlice.userSegmentRecap;

export default userSegmentSlice.reducer;
